import React, { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SEO from "../components/UI/SEO";
import SchemaMarkup from "../components/UI/SchemaMarkup";
import Loader from "../components/UI/Loader";
import BlogContent from "../components/Blog/BlogContent";
import BlogSidebar from "../components/Blog/BlogSidebar";
import useBlogs from "../hooks/useBlogs";

export default function BlogDetail() {
  const { slug } = useParams();
  const {
    selectedBlog,
    loadBlogBySlug,
    detailLoading,
    recentBlogs,
    categories,
    error,
  } = useBlogs({ page_size: 20 });

  useEffect(() => {
    loadBlogBySlug(slug);
  }, [slug, loadBlogBySlug]);

  const schema = useMemo(() => {
    if (!selectedBlog) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: selectedBlog.title,
      description: selectedBlog.meta_desc || selectedBlog.excerpt || "",
      image: selectedBlog.featured_image ? [selectedBlog.featured_image] : [],
      author: {
        "@type": "Person",
        name: selectedBlog.author_name || "Admin",
      },
      datePublished: selectedBlog.published_at || selectedBlog.created_at || "",
      mainEntityOfPage: `${window.location.origin}/blog/${selectedBlog.slug}`,
      publisher: {
        "@type": "Organization",
        name: "Solusi IoT Cerdas",
      },
    };
  }, [selectedBlog]);

  if (detailLoading) {
    return <Loader text="Memuat artikel..." />;
  }

  if (!selectedBlog) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Artikel tidak ditemukan</h1>
        <p className="mt-3 text-gray-600">{error || "Slug artikel tidak tersedia."}</p>
        <Link
          to="/blog"
          className="mt-6 inline-flex rounded-xl bg-orange-600 px-5 py-3 text-sm font-semibold text-white hover:bg-orange-700"
        >
          Kembali ke blog
        </Link>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${selectedBlog.meta_title || selectedBlog.title} | Solusi IoT Cerdas`}
        description={selectedBlog.meta_desc || selectedBlog.excerpt || ""}
        image={selectedBlog.featured_image}
        type="article"
        canonicalPath={`${window.location.origin}/blog/${selectedBlog.slug}`}
      />
      <SchemaMarkup schema={schema} />

      <section className="bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-orange-600">Beranda</Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-orange-600">Blog</Link>
            <span>/</span>
            <span className="text-gray-900">{selectedBlog.title}</span>
          </nav>

          <div className="mb-8 max-w-4xl">
            <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-medium text-orange-700">
              {selectedBlog.category_name || "Artikel"}
            </span>
            <h1 className="mt-4 text-4xl font-bold tracking-tight text-gray-900">
              {selectedBlog.title}
            </h1>
            {selectedBlog.subtitle ? (
              <p className="mt-4 text-lg leading-8 text-gray-600">{selectedBlog.subtitle}</p>
            ) : null}
          </div>

          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
            <BlogContent blog={selectedBlog} />
            <BlogSidebar
              categories={categories}
              recentPosts={recentBlogs.filter((item) => item.slug !== selectedBlog.slug)}
              activeCategory={selectedBlog.category_slug}
              onSelectCategory={() => {}}
            />
          </div>
        </div>
      </section>
    </>
  );
}
